import { Injectable } from '@angular/core';
import { Observable, from, map } from 'rxjs';
import { Affiliation, AffiliationRole } from './affiliation.model';
import { FirebaseApiProvider } from 'libs/api/providers/firebase-api.provider';

const COLLECTION = 'affiliations';

/**
 * Accès aux affiliations — pas de décision d'autorisation ici, voir
 * libs/authorization/authorization.service.ts pour ça. Volontairement non
 * câblé dans un guard tant que la Tranche A du sprint 2 n'est pas basculée.
 */
@Injectable({
  providedIn: 'root',
})
export class AffiliationService {
  constructor(private apiService: FirebaseApiProvider) {}

  /**
   * Lecture ponctuelle (`fetchAll`), pas un listener — même correctif que
   * `getAllAffiliations` : `collectionChanges()` ne tire jamais d'événement
   * quand le résultat est vide, ce qui bloquerait indéfiniment tout appelant
   * (dont `resolveEntryRoute`, appelé au démarrage pour chaque identité —
   * la quasi-totalité n'a aujourd'hui aucune affiliation).
   */
  getActiveAffiliations(identityId: string): Observable<Affiliation[]> {
    return from(
      this.apiService.fetchAll<Affiliation>(COLLECTION, Affiliation.fromObject, [
        { fieldPath: 'identityId', condition: '==', value: identityId },
        { fieldPath: 'status', condition: '==', value: 'active' },
      ]),
    );
  }

  /**
   * Actives ET terminées — nécessaire par ex. pour détecter un alumni.
   *
   * Lecture ponctuelle (`fetchAll`), pas un listener : `collectionChanges()`
   * (utilisé par `listenToChanges`) ne tire jamais le moindre événement
   * quand le résultat est — et reste — vide (confirmé en reproduisant avec
   * un identityId inexistant dans un onglet neuf : zéro émission, zéro
   * erreur, indéfiniment). Un guard de route n'a besoin que de l'état au
   * moment de la décision, jamais d'un flux live, donc ça règle le blocage
   * sans dépendre d'un correctif amont dans `FirebaseApiProvider`.
   */
  getAllAffiliations(identityId: string): Observable<Affiliation[]> {
    return from(
      this.apiService.fetchAll<Affiliation>(COLLECTION, Affiliation.fromObject, [
        { fieldPath: 'identityId', condition: '==', value: identityId },
      ]),
    );
  }

  getAffiliation(identityId: string, trainingId: string): Observable<Affiliation | null> {
    return this.apiService.fetchOne<Affiliation>(COLLECTION, Affiliation.buildId(identityId, trainingId), (data) =>
      data ? Affiliation.fromObject(data) : null,
    );
  }

  hasActiveRole(identityId: string, trainingId: string, role: AffiliationRole): Observable<boolean> {
    return this.getAffiliation(identityId, trainingId).pipe(
      map((affiliation) => !!affiliation && affiliation.status === 'active' && affiliation.role === role),
    );
  }

  /** Upsert : écrit toujours sur la clé déterministe, jamais un ID auto-généré. */
  async upsertAffiliation(affiliation: Affiliation): Promise<void> {
    const id = Affiliation.buildId(affiliation.identityId, affiliation.trainingId);
    await this.apiService.create<Affiliation>(COLLECTION, affiliation, Affiliation.toObject, id);
  }

  async endAffiliation(identityId: string, trainingId: string, endedAt: string): Promise<void> {
    const id = Affiliation.buildId(identityId, trainingId);
    await this.apiService.update(`${COLLECTION}/${id}`, { status: 'ended', endedAt }, (data) => data);
  }

  /**
   * Comme `endAffiliation`, mais n'echoue pas si l'affiliation n'existe pas
   * encore (relation creee avant que ce chemin n'ecrive dans `affiliations`,
   * ou par un chemin qui n'y ecrit pas encore) - a utiliser partout ou la
   * terminaison d'affiliation est un best-effort en marge d'une operation
   * legacy, jamais son bloqueur.
   */
  async endAffiliationIfExists(identityId: string, trainingId: string, endedAt: string): Promise<void> {
    try {
      await this.endAffiliation(identityId, trainingId, endedAt);
    } catch (e) {
      if (e?.code !== 'not-found') {
        throw e;
      }
    }
  }
}
